import { Food } from "@corpuscomplete/core/nutrition/food";
import { queries } from "@corpuscomplete/core/zero";
import { safeParseFloat } from "@corpuscomplete/core/utils";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export namespace FoodUI {
  export type FoodItemProps = {
    item: Food.Item;
    onSelect?: () => void;
    onSelectMethod?: "onPress" | "onPressIn";
    onRemove?: () => void;
    className?: string;
  };

  export type FoodItemLabelProps = {
    item: Food.Item;
    excludeServing?: boolean;
    mode?: "default" | "preparation";
  };

  export type FoodItemGroupProps = {
    label: string;
    items: readonly Food.Item[];
    variant?: "summary" | "dense" | "full";
    expanded?: boolean;
    defaultExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    renderActions?: (args: { expanded: boolean }) => ReactNode;
    renderContent?: (args: { expanded: boolean }) => ReactNode;
    className?: string;
  };

  export type UseItemInputOptions = {
    itemInput: Food.ItemInput;
    onEditItemAmount: (amount: number) => void;
    focusAmountInput: () => void;
  };

  export type UseItemInputResult = {
    item: Food.Item | undefined;
    servings: readonly Food.Serving[];
    amountInput: string;
    onChangeAmountInput: (amountInput: string) => void;
  };

  export function useItemInput({
    itemInput,
    onEditItemAmount,
    focusAmountInput,
  }: UseItemInputOptions): UseItemInputResult {
    const { amount, foodServingId } = itemInput;
    const [amountInput, setAmountInput] = useState(amount.toString());
    const lastAmountRef = useRef(amount);

    const [food] = useQuery(queries.food.byServingId({ foodServingId }));
    const servings = food?.servings ?? [];
    const item = useMemo(() => Food.inputToItem(itemInput, food), [itemInput, food]);

    useEffect(
      function syncAmountInput() {
        if (lastAmountRef.current !== amount) {
          lastAmountRef.current = amount;
          setAmountInput(amount.toString());
        }
      },
      [amount],
    );

    const servingIdRef = useRef(foodServingId);
    useEffect(
      function reFocusOnServingChange() {
        if (servingIdRef.current !== foodServingId) {
          servingIdRef.current = foodServingId;
          focusAmountInput?.();
        }
      },
      [foodServingId, focusAmountInput],
    );

    const onChangeAmountInput = useCallback(
      (nextAmountInput: string) => {
        setAmountInput(nextAmountInput);
        const nextAmount = safeParseFloat(nextAmountInput);
        if (nextAmount === null || nextAmount === amount) {
          return nextAmount;
        }
        lastAmountRef.current = nextAmount;
        onEditItemAmount?.(nextAmount);
        return nextAmount;
      },
      [amount, onEditItemAmount],
    );

    return {
      item,
      servings,
      amountInput,
      onChangeAmountInput,
    };
  }

  /**
   * Uses local queries to convert item inputs to items.
   * Actual queries for each food serving must be performed by the caller.
   */
  export function useInputsAsItems(inputs: readonly Food.ItemInput[]): readonly Food.Item[] {
    const [servings] = useQuery(Food.servingsByIds(inputs.map((input) => input.foodServingId)));
    return useMemo(() => {
      const items: Food.Item[] = [];
      for (const input of inputs) {
        const serving = servings.find((serving) => serving.id === input.foodServingId);
        const item = Food.asItem({ amount: input.amount, serving });
        if (item) {
          items.push(item);
        }
      }
      return items;
    }, [inputs, servings]);
  }
}
